import { TransactionProgress } from "./types"

export type TrackedOperation = {
  endpoint: string
  type: string
  title: string
  destinationPath: string
  /**
   * True when the endpoint is posted twice: once to validate (no `confirm` field, nothing is
   * queued) and again to run it. Only the second post is a background process, so the first
   * must not be tracked.
   *
   * Scoped per operation on purpose. Nearly every upload in the app posts a `file_upload`
   * field and none of the others carry `confirm`, so treating "file_upload without confirm"
   * as a dry run globally would silently stop tracking all of them.
   */
  twoPhase?: boolean
  /**
   * Send the user back to where they started rather than to a fixed page.
   *
   * The bulk uploads all end on one list, so a static `destinationPath` is right for them.
   * The payment schedule operations are launched from a particular schedule, and dropping the
   * user on the generic list would lose the thing they were working on. `destinationPath`
   * stays as the fallback for when the starting path was not recorded.
   */
  returnToSource?: boolean
}

const bulkOperations: TrackedOperation[] = [
  {
    endpoint: "/admin/employees/import",
    type: "upload employees",
    title: "Upload employees",
    destinationPath: "/dashboard/employees",
  },
  {
    endpoint: "/admin/employees/importupdates",
    type: "update employees",
    title: "Update employees",
    destinationPath: "/dashboard/employees",
  },
  {
    endpoint: "/admin/employees/import_account_details",
    type: "upload account details",
    title: "Upload account details",
    destinationPath: "/dashboard/employees",
  },
  {
    endpoint: "/admin/employees/import_split_payment_configuration",
    type: "update split payment configurations",
    title: "Update split payment configurations",
    destinationPath: "/dashboard/employees",
  },
  {
    endpoint: "/admin/employees/import_earning",
    type: "update employee earnings",
    title: "Update employee earnings",
    destinationPath: "/dashboard/employees",
  },
  {
    endpoint: "/admin/employees/import_rents",
    type: "upload employee rents",
    title: "Upload employee rents",
    destinationPath: "/dashboard/rents",
    twoPhase: true,
  },
  {
    endpoint: "/admin/employees/import_rent_updates",
    type: "update employee rents",
    title: "Update employee rents",
    destinationPath: "/dashboard/rents",
    twoPhase: true,
  },
  {
    endpoint: "/admin/payrolls/import_changes",
    type: "upload payroll changes",
    title: "Upload payroll changes",
    destinationPath: "/dashboard/payroll/payrollchange",
  },
  {
    endpoint: "/admin/payrolls/import_fusion_allowance_days",
    type: "upload fusion allowance days",
    title: "Upload fusion allowance days",
    destinationPath:
      "/dashboard/settings/integrations/oracle/configurations",
  },
  {
    endpoint: "/admin/payrolls/import_exemptions",
    type: "skip employees from payroll",
    title: "Skip employees from payroll",
    destinationPath: "/dashboard/payroll/payrollskip",
  },
  {
    endpoint: "/admin/benefits/bulk_enroll_hmoplan",
    type: "upload employee hmo plans",
    title: "Upload employee HMO plans",
    destinationPath: "/dashboard/benefits/medical",
  },
  {
    endpoint: "/admin/benefits/bulk_enroll_pfa",
    type: "upload employee pfas",
    title: "Upload employee PFAs",
    destinationPath: "/dashboard/benefits/pensions",
  },
  {
    endpoint: "/admin/contractors/import",
    type: "upload contractors",
    title: "Upload contractors",
    destinationPath: "/dashboard/contractor",
  },
  {
    endpoint: "/admin/contractors/importupdates",
    type: "update contractors",
    title: "Update contractors",
    destinationPath: "/dashboard/contractor",
  },
  {
    endpoint: "/admin/requests/import_exits",
    type: "upload exit requests",
    title: "Upload exit requests",
    destinationPath: "/dashboard/requests/list/exits",
  },
  {
    endpoint: "/admin/requests/import_redeployments",
    type: "upload redeployment requests",
    title: "Upload redeployment requests",
    destinationPath: "/dashboard/requests/list/redeployments",
  },
  {
    endpoint: "/admin/disciplinary/import_suspensions",
    type: "upload suspension requests",
    title: "Upload suspension requests",
    destinationPath: "/dashboard/disciplinary/suspensions",
  },
  {
    endpoint: "/admin/payment_schedules/import_custom_payment_schedule",
    type: "upload custom payment schedule",
    title: "Upload custom payment schedule",
    destinationPath: "/dashboard/payment-schedules/custom",
  },
  {
    endpoint: "/admin/custom_fields/bulk_upload_custom_field",
    type: "upload custom field responses",
    title: "Upload custom field responses",
    destinationPath: "/dashboard/settings/customization/custom-fields",
  },
  {
    endpoint: "/admin/time_tracking/import_absence",
    type: "upload employee absences",
    title: "Upload employee absences",
    destinationPath: "/dashboard/time-tracking/absence",
  },
  {
    endpoint: "/admin/company/edittaxactstatus",
    type: "update tax configuration",
    title: "Update Tax Configuration",
    destinationPath: "/dashboard/settings/customization",
  },
  // Bulk compensation imports. `type` must match TransactionProgressTypeParams on the
  // backend exactly - that string is how a progress event is matched to its process.
  {
    endpoint: "/admin/company/import_allowances",
    type: "upload allowances",
    title: "Upload allowances",
    destinationPath: "/dashboard/payroll/configuration/allowances",
    twoPhase: true,
  },
  {
    endpoint: "/admin/company/import_allowance_updates",
    type: "update allowances",
    title: "Update allowances",
    destinationPath: "/dashboard/payroll/configuration/allowances",
    twoPhase: true,
  },
  // Paybands are written strictly one at a time and each waits for its linked paygrades to
  // be refreshed, so these runs are legitimately long. The per-payband progress messages
  // are what make that read as working rather than hung.
  {
    endpoint: "/admin/company/import_paybands",
    type: "upload paybands",
    title: "Upload paybands",
    destinationPath: "/dashboard/payroll/configuration/paybands",
    twoPhase: true,
  },
  {
    endpoint: "/admin/company/import_payband_updates",
    type: "update paybands",
    title: "Update paybands",
    destinationPath: "/dashboard/payroll/configuration/paybands",
    twoPhase: true,
  },
  {
    endpoint: "/admin/company/import_paygrades",
    type: "upload paygrades",
    title: "Upload paygrades",
    destinationPath: "/dashboard/payroll/configuration/paygrades",
    twoPhase: true,
  },
  {
    endpoint: "/admin/company/import_paygrade_updates",
    type: "update paygrades",
    title: "Update paygrades",
    destinationPath: "/dashboard/payroll/configuration/paygrades",
    twoPhase: true,
  },
  // Payment schedules. These are not uploads -- they are long-running actions started from a
  // schedule -- but they report progress the same way, so the monitor tracks them alike.
  // `type` must match TransactionProgressTypeParams on the backend exactly.
  {
    endpoint: "/admin/payment_schedules/create_custom_payment_schedule",
    type: "create payment schedule",
    title: "Create payment schedule",
    destinationPath: "/dashboard/payment-schedules/custom",
    returnToSource: true,
  },
  {
    endpoint: "/admin/payment_schedules/update_custom_payment_schedule",
    type: "update payment schedule",
    title: "Update payment schedule",
    destinationPath: "/dashboard/payment-schedules/custom",
    returnToSource: true,
  },
  {
    endpoint: "/admin/payment_schedules/create_regular_payment_schedule",
    type: "create payment schedule",
    title: "Create payment schedule",
    destinationPath: "/dashboard/payment-schedules/regular",
    returnToSource: true,
  },
  {
    endpoint: "/admin/payment_schedules/process_payment_schedule",
    type: "process payment schedule",
    title: "Process payment schedule",
    destinationPath: "/dashboard/payment-schedules/custom",
    returnToSource: true,
  },
  {
    endpoint: "/admin/payment_schedules/retry_failed_payments",
    type: "process payment schedule",
    title: "Retry failed payments",
    destinationPath: "/dashboard/payment-schedules/custom",
    returnToSource: true,
  },
  {
    endpoint: "/admin/payment_schedules/refresh_payment_schedule_items_new",
    type: "refresh payment schedule",
    title: "Refresh payment schedule",
    destinationPath: "/dashboard/payment-schedules/custom",
    returnToSource: true,
  },
]

const operationByType = new Map(
  bulkOperations.map((operation) => [operation.type, operation])
)

const normalizeEndpoint = (url?: string) => {
  if (!url) return ""

  try {
    return new URL(url, window.location.origin).pathname
      .replace(/\/$/, "")
      .toLowerCase()
  } catch {
    return url.split("?")[0].replace(/\/$/, "").toLowerCase()
  }
}

export const findTrackedBulkOperation = (
  url?: string,
  method?: string
): TrackedOperation | undefined => {
  if ((method || "get").toLowerCase() !== "post") return undefined

  const endpoint = normalizeEndpoint(url)
  return bulkOperations.find((operation) => endpoint === operation.endpoint)
}

export const getTrackedBulkDestination = (
  operation: TrackedOperation,
  sourcePath?: string
) => {
  if (operation.returnToSource) {
    return sourcePath || operation.destinationPath
  }

  if (operation.type !== "upload fusion allowance days") {
    return operation.destinationPath
  }

  const sourceUrl = new URL(sourcePath || "", window.location.origin)
  const integrationId = sourceUrl.searchParams.get("x")
  if (!integrationId) return operation.destinationPath

  const destinationUrl = new URL(operation.destinationPath, window.location.origin)
  destinationUrl.searchParams.set("x", integrationId)
  destinationUrl.searchParams.set("tab", "Annual Leave In-lieu")
  return `${destinationUrl.pathname}?${destinationUrl.searchParams.toString()}`
}

export const getProcessDestination = (process: TransactionProgress) => {
  const type = process.type.trim().toLowerCase()
  const operation = operationByType.get(type)
  if (operation) {
    return getTrackedBulkDestination(operation, process.destinationPath)
  }

  if (process.destinationPath) return process.destinationPath

  if (type === "update payband" || type === "payband") {
    return `/dashboard/payroll/configuration/paybands/edit?x=${process.id}`
  }

  if (
    type === "prepare payroll" ||
    type === "refresh payroll" ||
    type === "submit payroll" ||
    type === "payroll"
  ) {
    const processCopy = `${process.title || ""} ${process.message || ""}`.toLowerCase()
    const isSubmission =
      type === "submit payroll" || /\bsubmit(?:ting|ted)?\b/.test(processCopy)

    return isSubmission
      ? `/dashboard/payroll/payrollrecord/view?key=${process.id}&view=rest`
      : `/dashboard/payroll/payperiod?key=${process.id}`
  }

  if (type === "update tax configuration") {
    return "/dashboard/settings/customization"
  }

  if (
    type === "create off-cycle pay schedule" ||
    type === "update off-cycle pay schedule"
  ) {
    return "/dashboard/payroll/configuration/pay-schedules?tab=off-cycle"
  }

  if (type === "update regular pay schedule") {
    return "/dashboard/payroll/configuration/pay-schedules?tab=regular"
  }

  return undefined
}
